import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ModelListPage } from '/imports/ui/ModelListPage';
import { modelClass, productionMeterType } from '/imports/api/productionMeters';
import { formatDateTime } from '/imports/utils';
import { strings } from '/imports/strings';
import { useHasProductionMeters } from '/imports/hooks';
import { PageHeading } from '/imports/ui/PageHeading';
import { ButtonDropdown } from '/imports/ui/ButtonDropdown';
import { Icon } from '/imports/ui/Icon';
import { Button } from '/imports/ui/Button';
import { PullProductionIntervalsModal } from '/imports/ui/PullProductionIntervalsModal';
import { ExportProductionIntervalsModal } from '/imports/ui/ExportProductionIntervalsModal';
import { toastOnError } from '/imports/ui/utils';
import { cacheAggregateProductionIntervals, productionIntervalGapsReport } from '/imports/api/productionIntervals';
import { MILLIS_1_YEAR } from '/imports/constants';

/** List page for production meters with bulk pull/export/cache/gap-report tools. */
export const ProductionMetersPage = () => {
  const canReadMeters = useHasProductionMeters();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showPullModal, setShowPullModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const cacheProduction = toastOnError(async () => {
    const startDate = prompt(strings.EnterTheStartDate(), (new Date(Date.now() - 2 * MILLIS_1_YEAR)).toISOString().split('T').at(0));
    const meterIds = selectedIds.length ? selectedIds : undefined;
    if (!startDate) return;
    await cacheAggregateProductionIntervals.call({ meterIds, startDate });
    toast.success(strings.Running());
  });
  const gapReport = toastOnError(async () => {
    const toastId = toast.success(strings.Running(), { autoClose: MILLIS_1_YEAR });
    const from = new Date(prompt(strings.StartDate()) || '2000-01-01');
    const to = new Date(prompt(strings.EndDate()) || '3000-01-01');
    const report = await productionIntervalGapsReport.call({meterIds: selectedIds.length? selectedIds : undefined, from, to});
    toast.dismiss(toastId);
    toast.info('See js console for the report data');
    console.log(report);
  });
  return <ModelListPage
    permission={() => canReadMeters}
    modelClass={modelClass}
    header={(
      <PageHeading
        left={<h1>{strings.ProductionMeters()}</h1>}
        right={
          <div className="buttons">
            {
              selectedIds.length ?
                <Button onClick={() => setShowPullModal(true)}>{strings.PullProduction()} ({selectedIds.length})</Button>
                : null
            }
            <ButtonDropdown
              label={<>{strings.New()}<Icon icon="keyboardArrowDown" /></>}
              items={[
                { key: 'egauge', label: 'Egauge', onClick: () => navigate('/production-meters/egauge') },
                { key: 'powerhub', label: 'Powerhub', onClick: () => navigate('/production-meters/powerhub') },
                { key: 'enphase', label: 'Enphase', onClick: () => navigate('/production-meters/enphase') },
                { key: 'solaredge', label: 'SolarEdge', onClick: () => navigate('/production-meters/solaredge') },
                { key: 'alsoenergy', label: 'AlsoEnergy', onClick: () => navigate('/production-meters/alsoenergy') },
                { key: 'sma', label: 'SMA', onClick: () => navigate('/production-meters/sma') },
                { key: 'smaClient', label: 'SMA Client', onClick: () => navigate('/production-meters/sma-client') },
                { key: 'solis', label: 'Solis', onClick: () => navigate('/production-meters/solis') },
                { key: 'fomware', label: 'FOMware', onClick: () => navigate('/production-meters/fomware') },
                { key: 'dummy', label: 'Dummy', onClick: () => navigate('/production-meters/new?type=dummy') },
              ]}
            />
            <ButtonDropdown
              label={<>{strings.Tools()}<Icon icon="keyboardArrowDown" /></>}
              items={[
                { key: 'exportProduction', label: strings.ExportProduction(), onClick: () => selectedIds.length ? setShowExportModal(true) : toast.info(strings.PleaseSelectMetersToExport()) },
                { key: 'viewReport', label: strings.ViewReport(), onClick: () => navigate('/production-meter-report') },
                { key: 'gapReport', label: strings.FindGaps(), onClick: () => gapReport() },
                { key: 'cache', label: strings.CacheProduction(), onClick: () => cacheProduction() },
              ]}
            />
            <>
              {
                showPullModal && selectedIds.length ?
                  <PullProductionIntervalsModal meterIds={selectedIds} onClose={() => setShowPullModal(false)} />
                  :
                  null
              }
              {
                showExportModal && selectedIds.length ?
                  <ExportProductionIntervalsModal meterIds={selectedIds} onClose={() => setShowExportModal(false)} />
                  :
                  null
              }
            </>
          </div>
        }
      />
    )}
    columns={[
      { field: 'name', render: (doc) => <Link to={`${modelClass.pathPrefix}/${doc._id}`}>{doc.name}</Link> },
      { field: 'identifier', render: (doc) => <span>{doc.identifier}</span> },
      { field: 'type', render: (doc) => <span>{productionMeterType(doc)}</span> },
      { field: 'commissioningDate', render: (doc) => <span>{doc.commissioningDate && formatDateTime(doc.commissioningDate)}</span> },
      { field: 'billingAccountNumber' },
      { field: 'createdAt', render: (doc) => <span>{doc.createdAt && formatDateTime(doc.createdAt)}</span> },
    ]}
    defaultSort={{ createdAt: -1 }}
    onSelectedIds={(selectedIds) => setSelectedIds(selectedIds)}
  />;
};